import { Box, Button, useDisclosure } from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import {
  ExpenseBudgetItemType,
  ProjectTypeEnum,
} from "@src/__generated__/urql-graphql";
import { Card } from "@src/components/ui-kit";
import { ModalCompleteExpense } from "@src/components/widgets/Modals/ModalCompleteExpense";
import { currency } from "@src/utils/formatters";
import { observer } from "mobx-react-lite";
import { Fragment, type FunctionComponent } from "react";
import type {
  ExpenseBudgetItemModel,
  ExpenseStructuredModel,
} from "../../models";
import { useStore } from "../../Store";
import GroupTitle from "../GroupTitle";
import type { ItemTitleProps } from "../ItemTitle";
import WithTotalBudget from "../WithTotalBudget";
import { ExpenseItem } from "./ExpenseItem";

type ExpenseListProps = {
  structuredItem: ExpenseStructuredModel;
} & Pick<ItemTitleProps, "tabIndex">;

const ExpenseList: FunctionComponent<ExpenseListProps> = ({
  structuredItem,
  tabIndex,
}) => {
  const store = useStore();

  const budgetItems = structuredItem.budgetItems.filter(
    (i) => i.type !== ExpenseBudgetItemType.Other,
  );
  const budgetItemsForTimeAndMaterials = structuredItem.budgetItems.filter(
    (i) => i.type === ExpenseBudgetItemType.Other,
  );
  const budgetItemOther = structuredItem.budgetItems.find(
    (i) => i.type === ExpenseBudgetItemType.Other,
  );

  if (!structuredItem.budgetItems.length) {
    return null;
  }

  return (
    <WithTotalBudget totalBudget={structuredItem.total_budget}>
      <Card
        overflowX={{
          base: "scroll",
          md: "unset",
        }}
        borderTopLeftRadius="unset"
        borderColor="gray.300"
        overflowY="hidden"
      >
        <Box minW={{ base: "1080px", md: "unset" }}>
          <Box borderBottom="1px" borderBottomColor="gray.300">
            <Card.Section>
              <GroupTitle>{structuredItem.name}</GroupTitle>
            </Card.Section>
          </Box>
          {store.project?.type === ProjectTypeEnum.TimeMaterials ? (
            <Fragment>
              {budgetItemsForTimeAndMaterials.map((bi) => (
                <ExpenseItem
                  key={bi.id}
                  item={bi}
                  visibleBudgetItemIds={budgetItemsForTimeAndMaterials.map(
                    ({ id }) => id,
                  )}
                  tabIndex={tabIndex}
                  profitText={
                    structuredItem.name === "Production" ||
                    structuredItem.name === "Media"
                      ? structuredItem.name
                      : undefined
                  }
                />
              ))}
            </Fragment>
          ) : (
            <Fragment>
              {budgetItems.map((bi) => (
                <ExpenseItem
                  key={bi.id}
                  item={bi}
                  visibleBudgetItemIds={budgetItems.map(({ id }) => id)}
                  tabIndex={tabIndex}
                  profitText={
                    structuredItem.name === "Production" ||
                    structuredItem.name === "Media"
                      ? structuredItem.name
                      : undefined
                  }
                />
              ))}

              {budgetItemOther && (
                <div className="bg-gray-200 py-3">
                  <Card.Section>
                    <BudgetItemOther budgetItemOther={budgetItemOther} />
                  </Card.Section>
                </div>
              )}
            </Fragment>
          )}
        </Box>
      </Card>
    </WithTotalBudget>
  );
};

const BudgetItemOther = ({
  budgetItemOther,
}: {
  budgetItemOther: ExpenseBudgetItemModel;
}) => {
  const store = useStore();
  const completeModal = useDisclosure();

  return (
    <div className="flex">
      <h3 className="font-bold text-2xl mr-8">
        <Trans>Other</Trans>
      </h3>
      <Button colorScheme="blue" onClick={completeModal.onOpen} variant="link">
        <span className="mr-3 Polaris-Link">
          <Trans>Received expenses:</Trans>{" "}
          {currency.formatByWorkspace(budgetItemOther.stats.real_expenses)}
        </span>
      </Button>
      <span className="text-gray-500 ml-3">
        <Trans>
          (Final commission:{" "}
          {currency.formatByWorkspace(budgetItemOther.stats.real_commission)})
        </Trans>
      </span>

      <ModalCompleteExpense
        id={budgetItemOther.id}
        {...completeModal}
        projectId={store.project?.id}
        onAction={() => {
          store.refetchGraph?.();
          store.refetchProject?.();
        }}
      />
    </div>
  );
};

export default observer(ExpenseList);
